import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip } from "@mui/material";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { fetchFutureFollowUps } from "../../../utils/api";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../../constants/statusColors";
import { formatShortDateKey } from "../../../utils/appDateTime";
import FollowUpFilters from "./FollowUpFilters";
import { patientIdOf } from "../email-center/EmailPatientPicker";

const formatDateOnly = (value) => {
  if (!value) return "N/A";
  return formatShortDateKey(value, String(value));
};

const FutureTab = ({ setError }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState("");

  const selectedPatientId = patientIdOf(selectedPatient);

  const loadFuture = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page,
        size: paginationModel.pageSize,
      };
      if (selectedPatientId) params.patientId = selectedPatientId;
      if (selectedBatchId) params.batchId = selectedBatchId;

      const data = await fetchFutureFollowUps(params);
      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      setRows(content.map((row, index) => ({
        ...row,
        id: row.followUpId || index,
        patientName: `${row.firstName || ""} ${row.lastName || ""}`.trim() || "N/A",
        accountNumber: row.patientAccountNumber || "N/A",
        barcode: row.collectionBatchId || "N/A",
        milestone: row.milestoneNumber ? `H${row.milestoneNumber}` : "N/A",
        dueDate: row.scheduledDueDate,
        status: row.followUpStatus || "PLANNED",
      })));
      setTotalRows(data?.totalElements || content.length);
    } catch (err) {
      console.error("Failed to load future follow-ups:", err);
      setError("Failed to load future plans.");
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, selectedBatchId, selectedPatientId, setError]);

  useEffect(() => {
    loadFuture();
  }, [loadFuture]);

  const applyBatchFilter = useCallback((nextBatchId) => {
    setSelectedBatchId(nextBatchId || "");
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const applyPatientFilter = useCallback((patient) => {
    setSelectedPatient(patient || null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedBatchId("");
    setSelectedPatient(null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const columns = useMemo(() => [
    { field: "patientName", headerName: "Participant", flex: 1 },
    { field: "accountNumber", headerName: "MRN", flex: 0.8 },
    { field: "barcode", headerName: "Barcode", flex: 0.8 },
    { field: "milestone", headerName: "Milestone", flex: 0.6 },
    {
      field: "dueDate",
      headerName: "Scheduled Due Date",
      flex: 1,
      renderCell: (params) => formatDateOnly(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={(params.value || "PLANNED").replace(/_/g, " ")}
          size="small"
          clickable={false}
          sx={{
            ...BASE_CHIP_SX,
            ...getStatusChipStyle(params.value),
            cursor: "default",
          }}
        />
      ),
    },
  ], []);

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexDirection: "column" }}>
      <FollowUpFilters
        batchId={selectedBatchId}
        onBatchIdChange={applyBatchFilter}
        selectedPatient={selectedPatient}
        onSelectedPatientChange={applyPatientFilter}
        onClear={clearFilters}
      />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          initialState={{ sorting: { sortModel: [{ field: "dueDate", sort: "asc" }] } }}
          pagination
          getRowId={(row) => row.id}
          disableSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default FutureTab;
